import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Download, FileText, Scale } from "lucide-react";

import { useLang, useLangPrefix } from "@/contexts/lang";

export const Route = createFileRoute("/about_/povazni-akti")({
	headers: () => ({
		"Cache-Control":
			"public, max-age=0, s-maxage=86400, stale-while-revalidate=604800",
	}),
	head: () => ({
		meta: [
			{ title: "Поважни акти | Македонијатурист АД" },
			{
				name: "description",
				content:
					"Листа на поважни акти, статути и деловници на Македонијатурист АД. Транспарентност и корпоративно управување.",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk/about/povazni-akti",
			},
		],
	}),
	component: PovazniAktiPage,
});

export function PovazniAktiPage() {
	const t = useLang();
	const prefix = useLangPrefix();

	return (
		<main className="bg-background">
			{/* Hero */}
			<section className="mt-grain relative overflow-hidden bg-primary text-white">
				<div className="pointer-events-none absolute inset-0">
					<div className="absolute -top-[20%] right-0 h-[600px] w-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.12),transparent)] blur-[100px]" />
					<div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
				</div>
				<div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16 md:py-28">
					<a
						href={`${prefix}/about`}
						className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-white/60 hover:text-white/90 transition-colors duration-300 mb-6 sm:mb-8"
					>
						<ArrowLeft className="h-3.5 w-3.5" />
						{t.povazniAkti.backLink}
					</a>
					<div className="max-w-3xl">
						<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">
							{t.povazniAkti.transparencyLabel}
						</p>
						<h1 className="mt-4 sm:mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08]">
							{t.povazniAkti.heading}
						</h1>
						<p className="mt-4 sm:mt-6 max-w-2xl text-base sm:text-lg text-white/80 font-light leading-relaxed">
							{t.povazniAkti.intro}
						</p>
					</div>
				</div>
			</section>

			{/* Documents */}
			<section className="bg-background py-12 sm:py-16 md:py-24">
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					<div className="mb-10 sm:mb-14">
						<span className="inline-block h-1 w-12 bg-accent mb-4 sm:mb-6" />
						<h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight text-primary leading-[1.1]">
							{t.povazniAkti.documentsHeading}
						</h2>
						<p className="mt-4 text-base text-foreground/70 font-light leading-relaxed max-w-2xl">
							{t.povazniAkti.documentsDesc}
						</p>
					</div>

					<div className="grid gap-10 sm:gap-14">
						{t.povazniAkti.categories.map((cat) => (
							<div key={cat.label}>
								<div className="flex items-center gap-3 mb-5 sm:mb-6">
									<Scale className="h-4 w-4 text-accent shrink-0" />
									<h3 className="font-display text-lg sm:text-xl font-medium text-primary">
										{cat.label}
									</h3>
								</div>
								<div className="grid gap-3 sm:gap-4 md:grid-cols-2">
									{cat.docs.map((doc) => (
										<div
											key={doc.name}
											className="group rounded-xl border border-border bg-card p-5 sm:p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-accent/30"
										>
											<div className="flex items-start gap-4">
												<div className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/5 text-primary">
													<FileText className="h-4 w-4" />
												</div>
												<div className="flex-1 min-w-0">
													<div className="flex items-start justify-between gap-3">
														<h4 className="font-medium text-primary text-sm sm:text-base leading-snug">
															{doc.name}
														</h4>
														<span className="shrink-0 rounded bg-secondary px-2 py-0.5 text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">
															{doc.type}
														</span>
													</div>
													<p className="mt-2 text-sm text-foreground/70 font-light leading-relaxed">
														{doc.description}
													</p>
													{doc.href ? (
														<a
															href={doc.href}
															download
															className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.12em] uppercase text-primary/60 hover:text-primary transition-colors duration-300"
														>
															<Download className="h-3.5 w-3.5" />
															{t.povazniAkti.download}
														</a>
													) : (
														<span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.12em] uppercase text-muted-foreground/40 cursor-not-allowed select-none">
															<Download className="h-3.5 w-3.5" />
															{t.povazniAkti.download}
														</span>
													)}
												</div>
											</div>
										</div>
									))}
								</div>
							</div>
						))}
					</div>

					{/* Note */}
					<div className="mt-12 sm:mt-16 rounded-xl md:rounded-2xl border border-border bg-secondary p-6 sm:p-8">
						<p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">
							{t.povazniAkti.noteLabel}
						</p>
						<p className="text-sm text-foreground/70 font-light leading-relaxed">
							{t.povazniAkti.noteText}
						</p>
					</div>

					{/* Contact */}
					<div className="mt-10 sm:mt-12 flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-sm text-muted-foreground font-light">
							{t.povazniAkti.notFoundNote}
						</p>
						<a
							href={`${prefix}/contact`}
							className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary/90"
						>
							{t.povazniAkti.contactBtn}
						</a>
					</div>
				</div>
			</section>
		</main>
	);
}
